import { KeyRound, UserPlus, Users, ArrowLeft, Search, UserX, UserCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROLES } from "utils/types";
import { addUser, listenToAllUsers, updateUser } from "firebaseService/firebaseService";
import { sendPasswordResetEmail } from "utils/utils";
import toast from "react-hot-toast";

const StaffManagementPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ id: '', name: '', email: '', department: '', role: 'employee', managerId: '' });
    const [addError, setAddError] = useState('');
    const [managerFilter, setManagerFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveTimeouts, setSaveTimeouts] = useState({});
    const [savingUsers, setSavingUsers] = useState(new Set());

    useEffect(() => {
        const unsubUsers = listenToAllUsers((users) => {
            setUsers(users);
        });
        return () => {
            unsubUsers();
            // Clear all pending timeouts
            Object.values(saveTimeouts).forEach(timeoutId => clearTimeout(timeoutId));
        };
    }, [saveTimeouts]);

    const managers = useMemo(() => users.filter(u => u.role === 'manager'), [users]);
    const activeManagersForAssignment = useMemo(() => managers.filter(m => m.isActive !== false), [managers]);

    const filteredUsers = useMemo(() => {
        let filtered = users;
        
        // By default, hide deactivated users (isActive === false). When showDeactivated is true, include all.
        if (!showDeactivated) {
            filtered = filtered.filter(user => user.isActive !== false);
        }
        
        // Apply manager filter
        if (managerFilter) {
            filtered = filtered.filter(user => user.managerId === managerFilter || user.id === managerFilter);
        }
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(user => 
                user.name.toLowerCase().includes(query) || 
                user.email.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    }, [users, managerFilter, searchQuery, showDeactivated]);

    // Function to build hierarchy with indentation
    const buildHierarchy = (users) => {
        const userMap = new Map(users.map(user => [user.id, { ...user, children: [] }]));
        const roots = [];

        users.forEach(user => {
            if (user.managerId && userMap.has(user.managerId)) {
                userMap.get(user.managerId).children.push(userMap.get(user.id));
            } else {
                roots.push(userMap.get(user.id));
            }
        });

        const result = [];
        const addToResult = (node, level = 0) => {
            result.push({ ...node, level });
            node.children.forEach(child => addToResult(child, level + 1));
        };

        roots.forEach(root => addToResult(root));
        return result;
    };

    const handleFieldChangeSave = async (userId, field, value) => {
        // Save immediately on blur (for name field)
        setSavingUsers(prev => new Set(prev).add(userId));
        try {
            const userToSave = users.find(u => u.id === userId);
            if (!userToSave) return;
            
            const updatedUser = { ...userToSave, [field]: value };
            
            // If updating name, regenerate initials
            if (field === 'name') {
                const initials = updatedUser.name
                    .match(/\b\w/g)
                    ?.join('')
                    .slice(0, 2)
                    .toUpperCase() || "";
                updatedUser.initials = initials;
            }
            
            await updateUser(userId, updatedUser);
            toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated for ${updatedUser.name}!`);
        } catch (error) {
            toast.error(`Failed to update ${field} for user. Please try again.`);
            console.error("Error updating user:", error);
            
            // Revert the local state on error
            setUsers(currentUsers =>
                currentUsers.map(user =>
                    user.id === userId ? { ...user, [field]: users.find(u => u.id === userId)?.[field] || '' } : user
                )
            );
        } finally {
            setSavingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const handleFieldChange = async (userId, field, value) => {
        // Update local state immediately for UI responsiveness
        setUsers(currentUsers =>
            currentUsers.map(user => {
                if (user.id === userId) {
                    const updated = { ...user, [field]: value };
                    // If updating name, regenerate initials immediately
                    if (field === 'name') {
                        const initials = updated.name
                            .match(/\b\w/g)
                            ?.join('')
                            .slice(0, 2)
                            .toUpperCase() || "";
                        updated.initials = initials;
                    }
                    return updated;
                }
                return user;
            })
        );

        // Clear existing timeout for this user
        if (saveTimeouts[userId]) {
            clearTimeout(saveTimeouts[userId]);
        }

        // Auto-save for role and manager changes immediately
        if (field === 'role' || field === 'managerId') {
            setSavingUsers(prev => new Set(prev).add(userId));
            try {
                const userToSave = users.find(u => u.id === userId);
                const updatedUser = { ...userToSave, [field]: value };
                await updateUser(userId, updatedUser);
                toast.success(`${field === 'role' ? 'Role' : 'Manager'} updated for ${updatedUser.name}!`);
            } catch (error) {
                toast.error(`Failed to update ${field === 'role' ? 'role' : 'manager'} for user. Please try again.`);
                console.error("Error updating user:", error);
                
                // Revert the local state on error
                setUsers(currentUsers =>
                    currentUsers.map(user =>
                        user.id === userId ? { ...user, [field]: users.find(u => u.id === userId)?.[field] || '' } : user
                    )
                );
            } finally {
                setSavingUsers(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(userId);
                    return newSet;
                });
            }
        } else if (field === 'name') {
            // For name field, don't auto-save - wait for blur event to save
            // Just update local state (already done above)
            return;
        } else {
            // Auto-save for other fields with debounce (1 second delay)
            const timeoutId = setTimeout(async () => {
                setSavingUsers(prev => new Set(prev).add(userId));
                try {
                    const userToSave = users.find(u => u.id === userId);
                    const updatedUser = { ...userToSave, [field]: value };
                    
                    await updateUser(userId, updatedUser);
                    toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated for ${updatedUser.name}!`);
                } catch (error) {
                    toast.error(`Failed to update ${field} for user. Please try again.`);
                    console.error("Error updating user:", error);
                    
                    // Revert the local state on error
                    setUsers(currentUsers =>
                        currentUsers.map(user =>
                            user.id === userId ? { ...user, [field]: users.find(u => u.id === userId)?.[field] || '' } : user
                        )
                    );
                } finally {
                    setSavingUsers(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(userId);
                        return newSet;
                    });
                }
            }, 1000);

            setSaveTimeouts(prev => ({ ...prev, [userId]: timeoutId }));
        }
    };
    
    const handleNewUserChange = (field, value) => {
        setNewUser(prev => ({ ...prev, [field]: value }));
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddError('');
        if (!newUser.name || !newUser.email) {
            setAddError("Please provide a Name and Email for the new user.");
            return;
        }
        
        setIsSubmitting(true);
        try {
            const newId = newUser.email.split('@')[0].toLowerCase();
            await addUser({ ...newUser, id: newId });
            toast.success(`User ${newUser.name} created successfully!`);
            setNewUser({ id: '', name: '', email: '', department: '', role: 'employee', managerId: '' });
        } catch (error) {
            console.error("Error adding user:", error);
            toast.error(`${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleActive = async (userId) => {
        const user = users.find(u => u.id === userId);
        if (!user) return;
        const newIsActive = !(user.isActive !== false);
        const action = newIsActive ? 'activate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${action} ${user.name}?`)) return;
        
        setSavingUsers(prev => new Set(prev).add(userId));
        try {
            await updateUser(userId, { ...user, isActive: newIsActive });
            toast.success(`${user.name} has been ${action}d successfully!`);
        } catch (error) {
            toast.error(`Failed to ${action} user. Please try again.`);
            console.error(`Error ${action}ing user:`, error);
        } finally {
            setSavingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const handlePasswordReset = async (email) => {
        if (!email) {
            toast.error("Please enter your email address first.");
            return;
        }
        try {
            await sendPasswordResetEmail(email);
            toast.success("Password reset email sent! Please check your inbox.");
        } catch (error) {
            console.error("Password reset error:", error);
            toast.error("Could not send password reset email. Please check the address is correct.");
        }
    };

    const hierarchicalUsers = buildHierarchy(filteredUsers);

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col gap-4 mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')} 
                                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <Users /> Staff Management
                            </h1>
                        </div>
                        
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <label htmlFor="managerFilter" className="text-sm font-medium text-gray-700 flex-shrink-0">
                                Filter by Manager:
                            </label>
                            <select 
                                id="managerFilter" 
                                value={managerFilter} 
                                onChange={(e) => setManagerFilter(e.target.value)} 
                                className="p-2 border rounded-md bg-white w-full md:w-auto"
                            >
                                <option value="">All Staff</option>
                                {managers.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* Search Bar */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                                <span className="text-xl">&times;</span>
                            </button>
                        )}
                    </div>
                    
                    {/* Show deactivated filter */}
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showDeactivated}
                                onChange={(e) => setShowDeactivated(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Show deactivated staff</span>
                        </label>
                    </div>
                    
                    {/* Results count */}
                    <div className="text-sm text-gray-600">
                        Showing {filteredUsers.length} of {users.length} staff members
                        {searchQuery && ` matching "${searchQuery}"`}
                        {!showDeactivated && users.some(u => u.isActive === false) && (
                            <span className="text-gray-500"> (excluding {users.filter(u => u.isActive === false).length} deactivated)</span>
                        )}
                    </div>
                </div>

                {/* Add New User Form */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <UserPlus size={20} /> Add New User
                    </h2>
                    <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                            <input
                                type="text"
                                value={newUser.name}
                                onChange={(e) => handleNewUserChange('name', e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Full Name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                value={newUser.email}
                                onChange={(e) => handleNewUserChange('email', e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="email@example.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                            <input
                                type="text"
                                value={newUser.department}
                                onChange={(e) => handleNewUserChange('department', e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Department"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={newUser.role}
                                onChange={(e) => handleNewUserChange('role', e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {ROLES.map(role => (
                                    <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                            <select
                                value={newUser.managerId}
                                onChange={(e) => handleNewUserChange('managerId', e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">No Manager</option>
                                {activeManagersForAssignment.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <UserPlus size={16} />
                                {isSubmitting ? 'Adding...' : 'Add User'}
                            </button>
                        </div>
                    </form>
                    {addError && <p className="text-red-600 text-sm mt-2">{addError}</p>}
                </div>

                {/* Staff List */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {hierarchicalUsers.length === 0 ? (
                        <div className="p-8 text-center">
                            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {searchQuery ? 'No staff found' : 'No staff members'}
                            </h3>
                            <p className="text-gray-500">
                                {searchQuery 
                                    ? `No staff members match "${searchQuery}". Try adjusting your search terms.`
                                    : 'No staff members have been added yet.'
                                }
                            </p>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {hierarchicalUsers.map((user) => (
                                    <tr key={user.id} className={`hover:bg-gray-50 ${savingUsers.has(user.id) ? 'bg-blue-50' : ''}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div 
                                                    className="w-2 h-2 bg-gray-300 rounded-full mr-3"
                                                    style={{ marginLeft: `${user.level * 20}px` }}
                                                />
                                                <input
                                                    type="text"
                                                    value={user.name}
                                                    onChange={(e) => handleFieldChange(user.id, 'name', e.target.value)}
                                                    onBlur={(e) => handleFieldChangeSave(user.id, 'name', e.target.value)}
                                                    className="text-sm font-medium text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none p-1 rounded"
                                                />
                                                {savingUsers.has(user.id) && (
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse ml-2" title="Saving..."></div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                                type="email"
                                                value={user.email}
                                                onChange={(e) => handleFieldChange(user.id, 'email', e.target.value)}
                                                className="text-sm text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none p-1 rounded"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                                type="text"
                                                value={user.department || ''}
                                                onChange={(e) => handleFieldChange(user.id, 'department', e.target.value)}
                                                className="text-sm text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none p-1 rounded"
                                            />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <select
                                                value={user.role}
                                                onChange={(e) => handleFieldChange(user.id, 'role', e.target.value)}
                                                className="text-sm text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none p-1 rounded"
                                            >
                                                {ROLES.map(role => (
                                                    <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <select
                                                value={user.managerId || ''}
                                                onChange={(e) => handleFieldChange(user.id, 'managerId', e.target.value)}
                                                className="text-sm text-gray-900 border-none bg-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none p-1 rounded"
                                            >
                                                <option value="">No Manager</option>
                                                {activeManagersForAssignment.filter(m => m.id !== user.id).map(m => (
                                                    <option key={m.id} value={m.id}>{m.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handlePasswordReset(user.email)}
                                                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                                    title="Reset password"
                                                >
                                                    <KeyRound size={16} />
                                                </button>
                                                {user.isActive !== false ? (
                                                    <button
                                                        onClick={() => handleToggleActive(user.id)}
                                                        className="text-amber-600 hover:text-amber-900 p-1 rounded hover:bg-amber-50"
                                                        title="Deactivate user"
                                                    >
                                                        <UserX size={16} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleToggleActive(user.id)}
                                                        className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                                        title="Activate user"
                                                    >
                                                        <UserCheck size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StaffManagementPage;
